import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { isAndroid, isDesktop, isIOS, isMacOs } from 'react-device-detect';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import Lottie from 'react-lottie-player';
import { Feature } from '../../hooks/use-feature';
import { useShake } from '../../hooks/use-shake';
import { layoutDefaults } from '../../utils/layout-defaults';
import { PaymentType, WalletType } from '../../utils/manager/card-manager';
import { computePercent } from '../../utils/manager/order-manager';
import { parseText, parseValue } from '../../utils/manager/price-manager';
import CurrencyInput from '../currency-input';
import payIconData from './pay-icon.json';

// const animate = 'animate__animated animate__shakeX animate__delay-1s';

function PaymentSelectAmountModalBody({
  amountLabel,
  methodLabel,
  defaultMethod,
  defaultAmountKey,
  defaultTipAmount,
  defaultRemainingFeeAmount,
  inputOptions,
  addressable,
  privateBill,
  prepaid,
  allOrders,
  selectedOrders,
  feature,
  remainingFees: remainingFeesParam,
  values,
  menuInfo,
  language,
  onChangeTip,
  onSelectMethod,
  onSelectAmount,
  onSelectRemainingFeeAmount,
}) {
  const { t } = useTranslation();
  const { isFeatureAvailable, isFeatureEnabled } = feature;
  const [tip, setTip] = useState();
  const [tipInput, setTipInput] = useState();
  const [tipVisible, setTipVisible] = useState(false);
  const [methodVisible, setMethodVisible] = useState(false);
  const [selectedAmountKey, setSelectedAmountKey] = useState();
  const [total, setTotal] = useState();
  const footerConteinerRef = useRef();
  const [defaultTip, setDefaultTip] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState();
  const [remainingFeeAmount, setRemainingFeeAmount] = useState(
    defaultRemainingFeeAmount ?? 0
  );
  const [remainingFees, setRemainingFees] = useState(remainingFeesParam);
  const symbol = menuInfo.i18n?.currency?.symbol;
  const tipSuggestionPercent = menuInfo.on_site?.tip?.suggestion ?? 0;

  const autoSelectAmount = useMemo(
    () =>
      addressable ||
      (privateBill && prepaid) ||
      allOrders.length === 1 ||
      selectedOrders.length === allOrders.length,
    [addressable, privateBill, prepaid, allOrders]
  );

  useEffect(() => {
    if (defaultAmountKey != null) {
      // selectAmountByKey(defaultAmountKey);
      setSelectedAmountKey(defaultAmountKey);
    }
  }, [defaultAmountKey]);

  useEffect(() => {
    if (defaultMethod != null) {
      setSelectedMethod(defaultMethod);
    }
  }, [defaultMethod]);

  useEffect(() => {
    if (selectedMethod != null) {
      setRemainingFeeAmountByMethod(selectedMethod);
      setTipVisible(false);
    }
  }, [selectedMethod]);

  // useEffect(() => {
  //   if (defaultTipAmount != null) {
  //     setTip(defaultTipAmount);
  //     setTipInput(parseValue(defaultTipAmount));
  //   }
  // }, [defaultTipAmount]);

  useEffect(() => {
    if (selectedAmountKey != null) {
      selectAmountByKey(selectedAmountKey);
      setMethodVisible(selectedAmountKey !== 'empty');
    }
  }, [selectedAmountKey]);

  useEffect(() => {
    if (autoSelectAmount) {
      // selectAmountByKey('allOrders');
      setSelectedAmountKey('allOrders');
    }
  }, [autoSelectAmount]);

  useEffect(() => {
    if (remainingFeeAmount != null) {
      onSelectRemainingFeeAmount?.call(this, remainingFeeAmount);
    }
  }, [remainingFeeAmount]);

  useEffect(() => {
    if (
      remainingFees != null &&
      selectedAmountKey != null &&
      selectedMethod != null
    ) {
      setRemainingFeeAmountByMethod(selectedMethod);
    }
  }, [remainingFees, selectedAmountKey, selectedMethod]);

  const paymentMethods = useMemo(() => {
    const methods = [];
    const fee = remainingFees[selectedAmountKey] ?? {};
    const value = values[selectedAmountKey] ?? 0;
    // const fee = {};

    // if (fee == null) return methods;

    if (isFeatureAvailable(Feature.PIX_PAYMENT)) {
      const feeAmount = fee.pix?.amount ?? 0;
      methods.push({
        value: PaymentType.PIX,
        label: `${t('payment.pay.pix')}: ${symbol} ${parseAmount(value, feeAmount)}`,
        disabled: !isFeatureEnabled(Feature.PIX_PAYMENT),
        remainingFeeAmount: feeAmount,
      });
    }

    if (isFeatureAvailable(Feature.WALLET_PAYMENT)) {
      const feeAmount = fee.wallet?.amount ?? 0;
      if (isIOS || isMacOs) {
        methods.push({
          value: WalletType.APPLE_PAY,
          label: `${t('payment.pay.wallet.apple')}: ${symbol} ${parseAmount(value, feeAmount)}`,
          disabled: !isFeatureEnabled(Feature.WALLET_PAYMENT),
          remainingFeeAmount: feeAmount,
        });
      }

      if (isAndroid || isDesktop) {
        methods.push({
          value: WalletType.GOOGLE_PAY,
          label: `${t('payment.pay.wallet.google')}: ${symbol} ${parseAmount(value, feeAmount)}`,
          disabled: !isFeatureEnabled(Feature.WALLET_PAYMENT),
          remainingFeeAmount: feeAmount,
        });
      }
    }

    if (isFeatureAvailable(Feature.CARD_PAYMENT)) {
      const feeAmount = fee.card?.amount ?? 0;
      methods.push({
        value: PaymentType.CARD,
        label: `${t('payment.pay.card')}: ${symbol} ${parseAmount(value, feeAmount)}`,
        disabled: !isFeatureEnabled(Feature.CARD_PAYMENT),
        remainingFeeAmount: feeAmount,
      });
    }

    if (prepaid && !addressable) {
      methods.push({
        value: 'offline',
        label: t('payment.prepaid.option'),
        disabled: false,
      });
    }

    if (_.isEmpty(methods)) {
      methods.push({
        value: 'empty',
        label: t('payment.pay.empty'),
        disabled: true,
      });
    }

    return methods;
  }, [remainingFees, selectedAmountKey, values]);

  useShake({
    element: footerConteinerRef?.current,
    loop: false,
    interval: 600,
  });

  const classes = useStyles({ total });

  useEffect(() => {
    if (!addressable) {
      const lTip = defaultTipAmount ?? defaultTip;

      setTip(lTip);
      setTipInput(parseValue(lTip));
    }
  }, [defaultTip, addressable, defaultTipAmount]);

  useEffect(() => {
    if (
      !addressable &&
      ((tip != null && defaultTip != null) ||
        (tip == null && defaultTip == null))
    ) {
      onChangeTip?.call(
        this,
        tip,
        computePercent(tip, defaultTip),
        setRemainingFees
      );
    }
  }, [tip, defaultTip, addressable]);

  useEffect(() => {
    if (selectedMethod != null) {
      onSelectMethod?.call(this, selectedMethod);
    }
  }, [selectedMethod]);

  function setRemainingFeeAmountByMethod(pSelectedMethod) {
    const method = paymentMethods.find((m) => m.value === pSelectedMethod);
    setRemainingFeeAmount(method?.remainingFeeAmount ?? 0);
  }

  function parseAmount(pValue, pRemainingFeeAmount) {
    if (pValue == null) return '';

    const amount = pValue + (pRemainingFeeAmount ?? 0);
    return `${parseValue(amount)}${pRemainingFeeAmount > 0 ? '*' : ''}`;
  }

  const onMethodChange = (event) => {
    const { value } = event.target;
    setSelectedMethod(value);
    // const method = paymentMethods.find((m) => m.value === value);
    // setRemainingFeeAmount(method?.remainingFeeAmount ?? 0);
  };

  const onAmountChange = (event) => {
    const key = event.target.value;
    // selectAmountByKey(key);
    setSelectedAmountKey(key);
  };

  function selectAmountByKey(key) {
    const amount = inputOptions[key] ?? _.head(_.values(inputOptions));

    setTotal(amount.value);
    setTipVisible(false);
    setDefaultTip(amount.tipSuggestion);

    onSelectAmount?.call(this, key, amount);
  }

  const onTipInputChange = (event) => {
    const text = event.target.value;
    setTipInput(text);

    const value = parseText(text?.replaceAll(symbol, '')) ?? 0;
    setTip(value);
  };

  const onSuggestionClick = () => {
    setTip(defaultTip);
    setTipInput(parseValue(defaultTip));
  };

  const tipFormmated = useMemo(() => parseValue(tip), [tip]);

  const totalFormmated = useMemo(
    () =>
      // TODO xxx
      parseValue(total + tip + remainingFeeAmount),
    [total, tip, remainingFeeAmount]
  );

  const percent = useMemo(
    () => computePercent(tip, defaultTip, tipSuggestionPercent),
    [tip, defaultTip, tipSuggestionPercent]
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Lottie
          play
          loop
          animationData={payIconData}
          className={classes.icon}
        />

        <FadeIn delay={150}>
          <div className={classes.title}>{t('payment')}</div>
          <div className={classes.subTitle}>
            {t(`payment.slogan${addressable ? '.delivery' : ''}`)}
          </div>
        </FadeIn>
      </div>
      {!autoSelectAmount && (
        <FadeIn>
          <Form.Control
            onChange={onAmountChange}
            defaultValue={defaultAmountKey ?? 'unselect'}
            as="select"
          >
            <option value="unselect" disabled>
              {amountLabel}
            </option>

            {_.keys(inputOptions).map((k) => (
              <option key={k} value={k}>
                {inputOptions[k].label}
              </option>
            ))}
          </Form.Control>
        </FadeIn>
      )}
      {methodVisible && (
        <FadeIn delay={150}>
          <Form.Control
            onChange={onMethodChange}
            as="select"
            defaultValue={defaultMethod ?? 'unselect'}
            // disabled={selectedAmountKey == null && defaultAmountKey == null}
          >
            <option value="unselect" disabled>
              {methodLabel}
            </option>

            {paymentMethods.map((method) => (
              <option
                key={method.value}
                value={method.value}
                disabled={method.disabled}
              >
                {method.label}
              </option>
            ))}

            {/* {prepaid && !addressable && (
          <option value="offline">{t('payment.prepaid.option')}</option>
        )} */}
          </Form.Control>
        </FadeIn>
      )}
      {!tipVisible &&
        !addressable &&
        isFeatureEnabled(Feature.ON_SITE_TIP) &&
        selectedMethod != null &&
        selectedMethod !== 'offline' &&
        total > 0 && (
          <div ref={footerConteinerRef}>
            <FadeIn
              className={classes.footerContent}
              // className={classNames(classes.footerContent, animate)}
              // style={{ '--animate-delay': '0.8s' }}
            >
              <table className={classes.table}>
                <tbody>
                  <tr>
                    <td>
                      {total > 0 && (
                        <>
                          <span
                            onClick={() => total && setTipVisible(true)}
                            className={total && classes.link}
                            aria-hidden
                          >
                            ({t('edit')})
                          </span>{' '}
                        </>
                      )}
                      {t('payment.service')} {symbol}:
                    </td>
                    <td className={classes.value}>{tipFormmated}</td>
                  </tr>
                  <tr>
                    <td className={classes.totalRow}>
                      {t('payment.total')} {symbol}:{' '}
                    </td>
                    <td className={classNames(classes.totalRow, classes.value)}>
                      {totalFormmated}
                    </td>
                  </tr>
                </tbody>
              </table>
            </FadeIn>
          </div>
        )}
      {tipVisible && !addressable && (
        <>
          <FadeIn transitionDuration={800}>
            <InputGroup>
              <InputGroup.Text>{percent}%</InputGroup.Text>
              <Form.Control
                as={CurrencyInput}
                i18n={menuInfo.i18n}
                language={language}
                onChange={onTipInputChange}
                onBlur={() => setTipInput(parseValue(tip))}
                placeholder={`${symbol} ${parseValue(0)}`}
                className={classes.input}
                value={tipInput}
              />
            </InputGroup>
          </FadeIn>
          <FadeIn
            className={classes.tipExplanationContainer}
            delay={500}
            transitionDuration={1200}
          >
            <div>
              <span className={classes.tipExplanation}>
                {t('payment.service-tax.explanation')}
              </span>
              {percent < _.parseInt(tipSuggestionPercent ?? 0) && (
                <>
                  {' '}
                  <span
                    className={classes.link}
                    onClick={onSuggestionClick}
                    aria-hidden
                  >
                    {t('payment.service-tax.suggestion', {
                      suggestion: _.parseInt(tipSuggestionPercent) ?? 0,
                    })}
                  </span>
                </>
              )}
            </div>
            <div className={classes.tipExplanationTotal}>
              {t('payment.total')}: {symbol} {totalFormmated}
            </div>
          </FadeIn>
        </>
      )}
      {!tipVisible && selectedMethod != null && remainingFeeAmount > 0 && (
        <FadeIn transitionDuration={800} delay={300}>
          <div className={classes.remainingFeeAmountExplanation}>
            *Adicional de processamento de pagamento digital na plataforma
          </div>
        </FadeIn>
      )}
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    // border: "1px solid red",
  },
  table: {
    // border: "1px solid red",
  },
  header: {
    marginBottom: layoutDefaults.biggerVerticalMargin,
    // border: "1px solid red",
  },
  icon: {
    height: 150,
    marginTop: -layoutDefaults.biggerHorizontalMargin,
    marginBottom: -layoutDefaults.smallVerticalMargin,
    // border: "1px solid blue",
  },
  title: {
    fontSize: layoutDefaults.biggestFontSize,
    fontWeight: 'bold',
    marginTop: layoutDefaults.smallerVerticalMargin,
  },
  subTitle: {
    fontSize: layoutDefaults.fontSize,
  },
  input: {
    // textAlign: "right",
  },
  link: {
    cursor: 'pointer',
    textDecorationLine: 'underline',
    fontSize: layoutDefaults.fontSize,
    color: layoutDefaults.linkColor,
    padding: 0,
    margin: 0,
    marginRight: layoutDefaults.smallerHorizontalMargin,
    // marginLeft: layoutDefaults.smallHorizontalMargin,
  },
  tipExplanationContainer: {
    textAlign: 'left',
    fontSize: layoutDefaults.smallFontSize,
  },
  tipExplanation: {
    opacity: layoutDefaults.opacity,
  },
  tipExplanationTotal: {
    textAlign: 'right',
    fontSize: layoutDefaults.fontSize,
    marginTop: layoutDefaults.biggerVerticalMargin,
    // border: "1px solid red",
  },
  value: {
    paddingLeft: layoutDefaults.smallerHorizontalMargin,
  },
  footerContent: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
    textAlign: 'right',
    // flex: 1,
    fontSize: layoutDefaults.fontSize,
    paddingTop: layoutDefaults.verticalMargin,
    paddingBottom: layoutDefaults.verticalMargin,
    paddingLeft: layoutDefaults.horizontalMargin,
    paddingRight: layoutDefaults.horizontalMargin,
    borderRadius: layoutDefaults.radius,
    border: '1px dashed #bbb',
  },
  totalRow: {
    fontWeight: 'bolder',
    // backgroundColor: "#bbb",
    paddingTop: layoutDefaults.smallVerticalMargin,
  },
  remainingFeeAmountExplanation: {
    fontSize: layoutDefaults.fontSize,
    textAlign: 'left',
    // textTransform: 'uppercase',
    marginTop: layoutDefaults.verticalMargin,
    opacity: layoutDefaults.opacity,
  },
});

export { PaymentSelectAmountModalBody };
